/**
 * Bayesian Knowledge Tracing: вероятность, что навык программы усвоен.
 *
 * Каждый ответ в дрилле навыка — наблюдение. Верный ответ поднимает
 * `pKnown` с поправкой на угадывание, ошибка опускает с поправкой на
 * оплошность; затем учитывается шанс выучить навык на этом шаге. Чистая
 * арифметика: состояние и ответ передаются аргументами.
 */

export interface BktParams {
  /** Априорная вероятность, что навык уже знаком. */
  readonly pInit: number;
  /** Шанс усвоить навык за одно упражнение. */
  readonly pLearn: number;
  /** Шанс ошибиться, зная навык. */
  readonly pSlip: number;
  /** Шанс угадать, не зная навык. */
  readonly pGuess: number;
}

export const DEFAULT_BKT: BktParams = {
  pInit: 0.2,
  pLearn: 0.15,
  pSlip: 0.1,
  pGuess: 0.2,
};

/** С этой вероятности навык считается освоенным. */
export const BKT_MASTERY = 0.95;

/** Апостериорная `pKnown` после одного ответа и шага обучения. */
export function updateKnowledge(pKnown: number, correct: boolean, params: BktParams = DEFAULT_BKT): number {
  const { pLearn, pSlip, pGuess } = params;
  const known = correct ? pKnown * (1 - pSlip) : pKnown * pSlip;
  const unknown = correct ? (1 - pKnown) * pGuess : (1 - pKnown) * (1 - pGuess);
  const posterior = known / (known + unknown);
  return posterior + (1 - posterior) * pLearn;
}

/** `pKnown` после серии ответов по порядку. */
export function traceKnowledge(
  answers: readonly boolean[],
  params: BktParams = DEFAULT_BKT,
  pStart: number = params.pInit,
): number {
  return answers.reduce((pKnown, correct) => updateKnowledge(pKnown, correct, params), pStart);
}

export function isMastered(pKnown: number): boolean {
  return pKnown >= BKT_MASTERY;
}
